// NOTE: `import type` is deliberate. A value import of zod here would pull the
// whole library into every bundle that touches an enum -- including the
// data-app bridge, which has a hard byte ceiling enforced by
// `packages/data-app/scripts/validate-bundle-externals.js`. The runtime branch
// below duck-types instead.
import type { z } from "zod";

type LiteralValues<T extends readonly z.ZodTypeAny[]> = {
  [K in keyof T]: T[K] extends z.ZodLiteral<infer U> ? U : never;
}[number];

/**
 * Takes a union of string literals and creates a pseudo enum object for ease of
 * referencing in code.
 *
 * Accepts a `z.enum`, a `z.union` of literals, or a lone `z.literal`: a union
 * built by spreading, or one with mixed literal types, does not collapse to an
 * enum but still needs its pseudo-enum object, and a one-member "union" is
 * written as a bare literal.
 */
export function getNormalEnum<T extends [string, ...string[]]>(
  schema: z.ZodEnum<T>,
): { [K in T[number]]: K };
export function getNormalEnum<
  T extends Readonly<[z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]>,
>(schema: z.ZodUnion<T>): { [K in Extract<LiteralValues<T>, string>]: K };
export function getNormalEnum<T extends string>(
  schema: z.ZodLiteral<T>,
): { [K in T]: K };
export function getNormalEnum(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: z.ZodEnum<any> | z.ZodUnion<any> | z.ZodLiteral<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  // `z.enum` exposes the mapping already; a union of literals does not, and a
  // lone literal has a `value` rather than `options`.
  if ("enum" in schema) {
    return schema.enum;
  }
  const options: { value: unknown }[] =
    "options" in schema
      ? (schema.options as { value: unknown }[])
      : [{ value: (schema as z.ZodLiteral<unknown>).value }];
  return options.reduce<Record<string, unknown>>((acc, opt) => {
    acc[opt.value as string] = opt.value;
    return acc;
  }, {});
}
